import React, { memo } from 'react';
import { CheckIcon, ShieldCheckIcon, MailIcon, PrinterIcon, InfinityIcon, StarIcon, ArrowDownIcon, ZapIcon, BrainIcon, ClipboardListIcon, TargetIcon, MedalIcon } from 'lucide-react';
import { CountdownTimer } from '../components/CountdownTimer';
import { FAQAccordion } from '../components/FAQAccordion';
const CHECKOUT_URL = 'https://checkout.totaliadigital.com/cart/52771143942512:1';
function CTAButton({
  children,
  className = '',
  scrollToOffer = false
}: {
  children: React.ReactNode;
  className?: string;
  scrollToOffer?: boolean;
}) {
  const handleClick = (e: React.MouseEvent) => {
    if (scrollToOffer) {
      e.preventDefault();
      const el = document.getElementById('compra');
      if (el) el.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <a href={scrollToOffer ? '#compra' : CHECKOUT_URL} target={scrollToOffer ? undefined : '_blank'} rel={scrollToOffer ? undefined : 'noopener noreferrer'} onClick={handleClick} className={`btn-cta inline-block text-white font-bold uppercase tracking-wide rounded-full px-8 py-5 text-lg md:text-xl shadow-xl cursor-pointer text-center transition-transform ${className}`} style={{
    backgroundColor: 'var(--color-orange)',
    fontFamily: 'var(--font-heading)',
    letterSpacing: '0.05em'
  }} data-id="element-30">
      {children}
    </a>;
}
function StarRating({
  count = 5
}: {
  count?: number;
}) {
  return <div className="flex items-center gap-1" data-id="element-31">
      {Array.from({
      length: count
    }).map((_, i) => <StarIcon key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-32" />)}
    </div>;
}
export function LandingPage() {
  return <div className="w-full min-h-screen bg-white" style={{
    fontFamily: 'var(--font-body)'
  }} data-id="element-33">
      {/* ── STICKY TOP URGENCY BAR ── */}
      <div className="sticky top-0 z-40 w-full py-2 px-4 text-center text-white text-sm font-semibold flex items-center justify-center gap-2" style={{
      backgroundColor: '#c0392b',
      fontFamily: 'var(--font-heading)'
    }} data-id="element-34">
        <span className="whitespace-nowrap" data-id="element-35">
          ⚡️ OFERTA SOLO POR HOY — Finalizá en:
        </span>
        <CountdownTimer compact className="text-white text-sm font-bold" data-id="element-36" />
      </div>

      {/* ── HERO SECTION ── */}
      <section className="w-full bg-white pt-10 pb-8 px-4" data-id="element-37">
        <div className="max-w-3xl mx-auto text-center" data-id="element-38">
          {/* Audience badge */}
          <div className="inline-block px-4 py-2 rounded-full text-white text-sm font-bold uppercase tracking-widest mb-6" style={{
          backgroundColor: 'var(--color-green-dark)',
          fontFamily: 'var(--font-heading)'
        }} data-id="element-39">
            Para Acompañantes Terapéuticos y Cuidadores
          </div>

          {/* Main headline */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-4 uppercase" style={{
          fontFamily: 'var(--font-heading)'
        }} data-id="element-40">
            El material que te ahorra horas de planificación y te permite llegar
            a cada encuentro con{' '}
            <span style={{
            color: 'var(--color-green-dark)'
          }} data-id="element-41">
              todo listo
            </span>
          </h1>

          {/* Product image — main mockup */}
          <div className="my-8" data-id="element-42">
            <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="inline-block" data-id="element-43">
              <img src="https://cdn.magicpatterns.com/uploads/iqEoGKKoRHKd9SXcDBnac6/mockup_principal.png" alt="Kit Mente Clara & Activa — Cuadernillo digital de estimulación cognitiva" className="w-full max-w-lg mx-auto drop-shadow-2xl cursor-pointer hover:scale-105 transition-transform duration-300" data-id="element-44" />
            </a>
          </div>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-gray-700 mb-6 leading-relaxed" data-id="element-45">
            Más de <strong data-id="element-46">150 actividades de gimnasia mental</strong> claras,
            guiadas y listas para usar con adultos mayores.{' '}
            <span className="font-semibold" style={{
            color: 'var(--color-green-dark)'
          }} data-id="element-47">
              Sin preparación previa. Sin improvisar.
            </span>
          </p>

          {/* Audience tags */}
          <div className="flex flex-wrap justify-center gap-3 mb-8" data-id="element-48">
            <span className="flex items-center gap-2 bg-green-50 text-green-800 px-4 py-2 rounded-full text-sm font-semibold border border-green-200" data-id="element-49">
              <CheckIcon className="w-4 h-4" data-id="element-50" /> Acompañantes Terapéuticos
            </span>
            <span className="flex items-center gap-2 bg-green-50 text-green-800 px-4 py-2 rounded-full text-sm font-semibold border border-green-200" data-id="element-51">
              <CheckIcon className="w-4 h-4" data-id="element-52" /> Cuidadores domiciliarios
            </span>
            <span className="flex items-center gap-2 bg-green-50 text-green-800 px-4 py-2 rounded-full text-sm font-semibold border border-green-200" data-id="element-53">
              <CheckIcon className="w-4 h-4" data-id="element-54" /> Trabajadores de geriátrico
            </span>
          </div>

          {/* Social proof */}
          <div className="flex items-center justify-center gap-3 mb-8" data-id="element-55">
            <StarRating data-id="element-56" />
            <span className="text-gray-700 font-semibold" data-id="element-57">
              4.9/5 · Más de 200 acompañantes ya lo usan
            </span>
          </div>

          {/* Primary CTA */}
          <CTAButton scrollToOffer className="w-full max-w-md mx-auto block text-xl py-6" data-id="element-58">
            ¡QUIERO EL KIT AHORA!
          </CTAButton>

          {/* Trust badges */}
          <div className="flex items-center justify-center gap-6 mt-5 text-gray-500 text-sm flex-wrap" data-id="element-59">
            <span className="flex items-center gap-1.5" data-id="element-60">
              <ShieldCheckIcon className="w-4 h-4 text-green-600" data-id="element-61" />
              Compra 100% segura
            </span>
            <span className="flex items-center gap-1.5" data-id="element-62">
              <MailIcon className="w-4 h-4 text-green-600" data-id="element-63" />
              Acceso inmediato por mail
            </span>
          </div>
        </div>
      </section>

      {/* ── PAIN POINTS ── */}
      <section className="w-full bg-gray-50 py-16 px-4" data-id="element-64">
        <div className="max-w-3xl mx-auto" data-id="element-65">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4 uppercase" style={{
          fontFamily: 'var(--font-heading)'
        }} data-id="element-66">
            ¿Te suena alguna de estas situaciones?
          </h2>
          <p className="text-center text-gray-600 mb-10 text-lg" data-id="element-67">
            Si trabajás con adultos mayores, probablemente sí.
          </p>

          <div className="space-y-4" data-id="element-68">
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-69">
              <span className="text-2xl flex-shrink-0" data-id="element-70">😮‍💨</span>
              <p className="text-gray-700 leading-relaxed" data-id="element-71">
                <strong data-id="element-72">
                  Llegás al encuentro pensando qué actividad hacer...
                </strong>{' '}
                y nada te convence del todo.
              </p>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-73">
              <span className="text-2xl flex-shrink-0" data-id="element-74">🔍</span>
              <p className="text-gray-700 leading-relaxed" data-id="element-75">
                <strong data-id="element-76">Buscás material online</strong> y lo que encontrás es
                infantil, demasiado clínico o requiere adaptación constante que
                no tenés tiempo de hacer.
              </p>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-77">
              <span className="text-2xl flex-shrink-0" data-id="element-78">🔄</span>
              <p className="text-gray-700 leading-relaxed" data-id="element-79">
                <strong data-id="element-80">Terminás repitiendo las mismas actividades</strong>{' '}
                porque crear algo nuevo lleva tiempo que no sobra.
              </p>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-81">
              <span className="text-2xl flex-shrink-0" data-id="element-82">😤</span>
              <p className="text-gray-700 leading-relaxed" data-id="element-83">
                <strong data-id="element-84">
                  Cuando la actividad no engancha, el encuentro se desarma.
                </strong>{' '}
                Y eso te pesa.
              </p>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-85">
              <span className="text-2xl flex-shrink-0" data-id="element-86">🧠</span>
              <p className="text-gray-700 leading-relaxed" data-id="element-87">
                <strong data-id="element-88">
                  Querés ofrecer estimulación cognitiva real, con base
                  científica,
                </strong>{' '}
                pero no siempre encontrás material que cumpla eso.
              </p>
            </div>
          </div>

          <div className="mt-10 rounded-xl p-6 text-center text-white" style={{
          backgroundColor: 'var(--color-green-dark)'
        }} data-id="element-89">
            <p className="text-xl font-bold" style={{
            fontFamily: 'var(--font-heading)'
          }} data-id="element-90">
              El problema no sos vos.
            </p>
            <p className="mt-2 text-green-100 text-lg" data-id="element-91">
              Es que nadie te da una herramienta realmente lista para usar.
            </p>
          </div>
        </div>
      </section>

      {/* ── BRIDGE / SOLUTION ── */}
      <section className="w-full py-16 px-4 text-white" style={{
      backgroundColor: 'var(--color-green-dark)'
    }} data-id="element-92">
        <div className="max-w-3xl mx-auto text-center" data-id="element-93">
          <h2 className="text-3xl md:text-4xl font-bold uppercase mb-2" style={{
          fontFamily: 'var(--font-heading)'
        }} data-id="element-94">
            Por eso creamos el
          </h2>
          <h2 className="text-4xl md:text-5xl font-bold uppercase mb-4" style={{
          fontFamily: 'var(--font-heading)',
          color: 'var(--color-gold)'
        }} data-id="element-95">
            KIT MENTE CLARA & ACTIVA 🧠
          </h2>
          <p className="text-green-100 text-lg" data-id="element-96">
            El cuadernillo digital de estimulación cognitiva diseñado para ser
            usado directamente, sin preparación previa.
          </p>
        </div>
      </section>

      {/* ── PRODUCT FEATURES ── */}
      <section className="w-full bg-white py-16 px-4" data-id="element-97">
        <div className="max-w-3xl mx-auto" data-id="element-98">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12 uppercase" style={{
          fontFamily: 'var(--font-heading)'
        }} data-id="element-99">
            ¿Qué incluye el kit?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-100">
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-101">
              <div className="text-3xl mb-3" data-id="element-102">📋</div>
              <h3 className="font-bold text-gray-900 text-lg mb-2" style={{
              fontFamily: 'var(--font-heading)'
            }} data-id="element-103">
                +150 actividades en 23 temáticas
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-104">
                Organizadas por tipo de estimulación: atención, memoria,
                coordinación, percepción visual y lógica.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-105">
              <div className="text-3xl mb-3" data-id="element-106">✅</div>
              <h3 className="font-bold text-gray-900 text-lg mb-2" style={{
              fontFamily: 'var(--font-heading)'
            }} data-id="element-107">
                Instrucciones claras incluidas
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-108">
                El adulto mayor puede seguirlas solo o con mínima guía. Cero
                preparación extra de tu parte.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-109">
              <div className="text-3xl mb-3" data-id="element-110">🎯</div>
              <h3 className="font-bold text-gray-900 text-lg mb-2" style={{
              fontFamily: 'var(--font-heading)'
            }} data-id="element-111">
                Adaptable a distintos niveles
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-112">
                Elegís las actividades que mejor se ajustan a cada paciente, sin
                necesidad de modificar nada.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-113">
              <div className="text-3xl mb-3" data-id="element-114">🖨️</div>
              <h3 className="font-bold text-gray-900 text-lg mb-2" style={{
              fontFamily: 'var(--font-heading)'
            }} data-id="element-115">
                PDF listo para imprimir o usar en pantalla
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-116">
                Descargás y empezás. Sin apps, sin plataformas, sin
                complicaciones.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-117">
              <div className="text-3xl mb-3" data-id="element-118">♾️</div>
              <h3 className="font-bold text-gray-900 text-lg mb-2" style={{
              fontFamily: 'var(--font-heading)'
            }} data-id="element-119">
                Imprimís sin límite
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-120">
                Un solo pago y podés usarlo con todos tus pacientes todas las
                veces que necesites.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-121">
              <div className="text-3xl mb-3" data-id="element-122">🏅</div>
              <h3 className="font-bold text-gray-900 text-lg mb-2" style={{
              fontFamily: 'var(--font-heading)'
            }} data-id="element-123">
                Sin material infantil
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-124">
                Actividades diseñadas para adultos. Respetan la dignidad e
                inteligencia del mayor.
              </p>
            </div>
          </div>

          {/* Scientific backing */}
          <div className="mt-8 rounded-xl p-6 border-l-4 text-white" style={{
          backgroundColor: 'var(--color-green-dark)',
          borderLeftColor: 'var(--color-gold)'
        }} data-id="element-125">
            <p className="font-bold text-lg mb-1" style={{
            fontFamily: 'var(--font-heading)'
          }} data-id="element-126">
              📐 Con respaldo científico
            </p>
            <p className="text-green-100 text-sm leading-relaxed" data-id="element-127">
              Basado en Neuroplasticidad, Reserva Cognitiva, modelo de Sohlberg
              & Mateer y Gimnasia Cerebral de Paul Dennison. Podés mencionarlo
              en cualquier plan de trabajo o informe profesional.
            </p>
          </div>
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section className="w-full bg-gray-50 py-16 px-4" data-id="element-128">
        <div className="max-w-3xl mx-auto" data-id="element-129">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4 uppercase" style={{
          fontFamily: 'var(--font-heading)'
        }} data-id="element-130">
            Con este kit vas a poder...
          </h2>
          <p className="text-center text-gray-500 mb-10" data-id="element-131">
            Resultados concretos para tu trabajo diario
          </p>

          <div className="space-y-4" data-id="element-132">
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-133">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
              backgroundColor: 'var(--color-green-dark)'
            }} data-id="element-134">
                <CheckIcon className="w-4 h-4 text-white" data-id="element-135" />
              </div>
              <div data-id="element-136">
                <p className="font-bold text-gray-900" data-id="element-137">
                  Llegar a cada encuentro con todo listo
                </p>
                <p className="text-gray-600 text-sm mt-1" data-id="element-138">
                  — sin improvisar, sin buscar, sin adaptaciones de urgencia.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-139">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
              backgroundColor: 'var(--color-green-dark)'
            }} data-id="element-140">
                <CheckIcon className="w-4 h-4 text-white" data-id="element-141" />
              </div>
              <div data-id="element-142">
                <p className="font-bold text-gray-900" data-id="element-143">
                  Ofrecer actividades que respetan al adulto mayor
                </p>
                <p className="text-gray-600 text-sm mt-1" data-id="element-144">
                  — nada infantil, nada condescendiente.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-145">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
              backgroundColor: 'var(--color-green-dark)'
            }} data-id="element-146">
                <CheckIcon className="w-4 h-4 text-white" data-id="element-147" />
              </div>
              <div data-id="element-148">
                <p className="font-bold text-gray-900" data-id="element-149">
                  Justificar el material con base científica
                </p>
                <p className="text-gray-600 text-sm mt-1" data-id="element-150">
                  — incluilo en cualquier plan de trabajo o informe profesional.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-151">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
              backgroundColor: 'var(--color-green-dark)'
            }} data-id="element-152">
                <CheckIcon className="w-4 h-4 text-white" data-id="element-153" />
              </div>
              <div data-id="element-154">
                <p className="font-bold text-gray-900" data-id="element-155">
                  Reducir la carga de planificación
                </p>
                <p className="text-gray-600 text-sm mt-1" data-id="element-156">
                  — y enfocarte en el acompañamiento real.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-157">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
              backgroundColor: 'var(--color-green-dark)'
            }} data-id="element-158">
                <CheckIcon className="w-4 h-4 text-white" data-id="element-159" />
              </div>
              <div data-id="element-160">
                <p className="font-bold text-gray-900" data-id="element-161">
                  Ver al adulto mayor comprometido y activo
                </p>
                <p className="text-gray-600 text-sm mt-1" data-id="element-162">
                  — lo que valida tu trabajo y fortalece el vínculo.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-163">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
              backgroundColor: 'var(--color-green-dark)'
            }} data-id="element-164">
                <CheckIcon className="w-4 h-4 text-white" data-id="element-165" />
              </div>
              <div data-id="element-166">
                <p className="font-bold text-gray-900" data-id="element-167">
                  Trabajar con más calma y seguridad
                </p>
                <p className="text-gray-600 text-sm mt-1" data-id="element-168">
                  — porque llegás preparado a cada sesión.
                </p>
              </div>
            </div>
          </div>

          {/* Mid-page CTA */}
          <div className="mt-12 text-center" data-id="element-169">
            <CTAButton scrollToOffer className="w-full max-w-md mx-auto block" data-id="element-170">
              ¡SÍ, QUIERO TRABAJAR MEJOR AHORA!
            </CTAButton>
          </div>
        </div>
      </section>

      {/* ── BONUSES ── */}
      <section className="w-full py-16 px-4 text-white" style={{
      backgroundColor: 'var(--color-green-dark)'
    }} data-id="element-171">
        <div className="max-w-3xl mx-auto" data-id="element-172">
          <div className="text-center mb-10" data-id="element-173">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-bold uppercase tracking-widest mb-4" style={{
            backgroundColor: 'var(--color-gold)',
            color: '#1a1a1a',
            fontFamily: 'var(--font-heading)'
          }} data-id="element-174">
              🎁 Bonus Exclusivos — Solo por hoy
            </span>
            <h2 className="text-3xl md:text-4xl font-bold uppercase mb-3" style={{
            fontFamily: 'var(--font-heading)'
          }} data-id="element-175">
              Además, con tu compra hoy recibís estos BONUS GRATIS
            </h2>
            <p className="text-green-200 text-lg" data-id="element-176">
              Material adicional para que nunca te falten actividades — ideal
              para encuentros extra o trabajo del adulto en casa.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-177">
            {/* Bonus 1 */}
            <div className="bg-white/10 rounded-2xl p-6 border border-white/20 text-center" data-id="element-178">
              <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" data-id="element-179">
                <img src="https://cdn.magicpatterns.com/uploads/bmUw6K326hT1kzvwV69y9D/mockup_bonus_1.png" alt="Bonus 1: Sudokus Progresivos para Adultos Mayores" className="w-full aspect-square object-cover rounded-xl mb-4 cursor-pointer hover:scale-105 transition-transform duration-300" style={{
                maxWidth: '300px',
                margin: '0 auto 1rem'
              }} data-id="element-180" />
              </a>
              <div className="inline-block px-3 py-1 rounded-full text-sm font-bold uppercase mb-3" style={{
              backgroundColor: 'var(--color-gold)',
              color: '#1a1a1a',
              fontFamily: 'var(--font-heading)'
            }} data-id="element-181">
                GRATIS — Solo hoy
              </div>
              <div className="text-2xl mb-2" data-id="element-182">🔢</div>
              <h3 className="font-bold text-xl mb-2" style={{
              fontFamily: 'var(--font-heading)'
            }} data-id="element-183">
                Sudokus Progresivos para Adultos Mayores
              </h3>
              <p className="text-green-200 text-sm leading-relaxed" data-id="element-184">
                Distintos niveles de dificultad para entrenar concentración,
                lógica y agilidad mental de forma entretenida.
              </p>
            </div>

            {/* Bonus 2 */}
            <div className="bg-white/10 rounded-2xl p-6 border border-white/20 text-center" data-id="element-185">
              <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" data-id="element-186">
                <img src="https://cdn.magicpatterns.com/uploads/kQxvVWYyKQjv9XfhGdhVw4/mockup_bonus_2.png" alt="Bonus 2: Sopa de Letras ActivaMente" className="w-full aspect-square object-cover rounded-xl mb-4 cursor-pointer hover:scale-105 transition-transform duration-300" style={{
                maxWidth: '300px',
                margin: '0 auto 1rem'
              }} data-id="element-187" />
              </a>
              <div className="inline-block px-3 py-1 rounded-full text-sm font-bold uppercase mb-3" style={{
              backgroundColor: 'var(--color-gold)',
              color: '#1a1a1a',
              fontFamily: 'var(--font-heading)'
            }} data-id="element-188">
                GRATIS — Solo hoy
              </div>
              <div className="text-2xl mb-2" data-id="element-189">🔤</div>
              <h3 className="font-bold text-xl mb-2" style={{
              fontFamily: 'var(--font-heading)'
            }} data-id="element-190">
                Sopa de Letras ActivaMente
              </h3>
              <p className="text-green-200 text-sm leading-relaxed" data-id="element-191">
                Actividades de búsqueda de palabras que fortalecen memoria,
                atención y velocidad mental.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING / OFFER BOX ── */}
      <section id="compra" className="w-full bg-white py-16 px-4" data-id="element-192">
        <div className="max-w-2xl mx-auto" data-id="element-193">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4 uppercase" style={{
          fontFamily: 'var(--font-heading)'
        }} data-id="element-194">
            🎁 Todo lo que recibís hoy
          </h2>
          <p className="text-center text-gray-600 mb-8" data-id="element-195">
            Kit completo + bonus exclusivos por un único pago
          </p>

          <div className="border-4 rounded-2xl overflow-hidden shadow-2xl" style={{
          borderColor: 'var(--color-orange)'
        }} data-id="element-196">
            {/* Offer header */}
            <div className="py-4 px-6 text-center text-white" style={{
            backgroundColor: 'var(--color-orange)'
          }} data-id="element-197">
              <p className="font-bold text-lg uppercase tracking-wide" style={{
              fontFamily: 'var(--font-heading)'
            }} data-id="element-198">
                🔴 Oferta por tiempo limitado — vence cuando termina el contador
              </p>
            </div>

            <div className="p-8 bg-white" data-id="element-199">
              {/* Product name */}
              <div className="text-center mb-6" data-id="element-200">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 uppercase mb-1" style={{
                fontFamily: 'var(--font-heading)'
              }} data-id="element-201">
                  🧠 KIT MENTE CLARA & ACTIVA
                </h3>
                <p className="text-gray-500" data-id="element-202">
                  +150 Actividades · 2 Bonus de regalo
                </p>
              </div>

              {/* Pricing */}
              <div className="text-center mb-6" data-id="element-203">
                <p className="text-gray-400 line-through text-xl" data-id="element-204">
                  $37.900 ARS
                </p>
                <p className="text-5xl md:text-6xl font-bold" style={{
                fontFamily: 'var(--font-heading)',
                color: 'var(--color-green-dark)'
              }} data-id="element-205">
                  $18.790
                </p>
                <p className="text-green-600 font-semibold mt-1" data-id="element-206">
                  Ahorrás $19.110
                </p>
                <p className="text-gray-500 text-sm mt-1" data-id="element-207">
                  Hasta 3 cuotas fijas sin interés
                </p>
              </div>

              {/* Countdown */}
              <div className="flex justify-center mb-6" data-id="element-208">
                <div className="rounded-xl px-6 py-4 text-center" style={{
                backgroundColor: 'var(--color-green-dark)'
              }} data-id="element-209">
                  <p className="text-white text-sm mb-2 uppercase tracking-wide font-semibold" style={{
                  fontFamily: 'var(--font-heading)'
                }} data-id="element-210">
                    Esta oferta vence en:
                  </p>
                  <CountdownTimer data-id="element-211" />
                </div>
              </div>

              {/* What's included */}
              <div className="bg-gray-50 rounded-xl p-5 mb-6 space-y-2" data-id="element-212">
                <div className="flex items-center gap-3" data-id="element-213">
                  <CheckIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-214" />
                  <span className="text-gray-700 font-medium" data-id="element-215">
                    Kit Mente Clara & Activa (+150 actividades)
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-216">
                  <CheckIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-217" />
                  <span className="text-gray-700 font-medium" data-id="element-218">
                    BONUS: Sudokus Progresivos{' '}
                    <span className="text-green-600 font-bold" data-id="element-219">(GRATIS)</span>
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-220">
                  <CheckIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-221" />
                  <span className="text-gray-700 font-medium" data-id="element-222">
                    BONUS: Sopa de Letras ActivaMente{' '}
                    <span className="text-green-600 font-bold" data-id="element-223">(GRATIS)</span>
                  </span>
                </div>
              </div>

              <p className="text-center text-gray-500 text-sm mb-4" data-id="element-224">
                Valor total del pack:{' '}
                <span className="line-through" data-id="element-225">$77.900 ARS</span>
              </p>

              {/* Main CTA */}
              <CTAButton className="w-full block text-xl py-6" data-id="element-226">
                ✅ SÍ, QUIERO EL KIT AHORA
              </CTAButton>

              {/* Trust row */}
              <div className="flex items-center justify-center gap-4 mt-5 text-gray-500 text-sm flex-wrap" data-id="element-227">
                <span className="flex items-center gap-1.5" data-id="element-228">
                  <MailIcon className="w-4 h-4 text-green-600" data-id="element-229" />
                  Acceso inmediato
                </span>
                <span className="flex items-center gap-1.5" data-id="element-230">
                  <ShieldCheckIcon className="w-4 h-4 text-green-600" data-id="element-231" />
                  Un solo pago
                </span>
                <span className="flex items-center gap-1.5" data-id="element-232">
                  <PrinterIcon className="w-4 h-4 text-green-600" data-id="element-233" />
                  Descarga digital
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="w-full bg-gray-50 py-16 px-4" data-id="element-234">
        <div className="max-w-3xl mx-auto" data-id="element-235">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-3 uppercase" style={{
          fontFamily: 'var(--font-heading)'
        }} data-id="element-236">
            Lo que dicen quienes ya lo usan ❤️
          </h2>
          <p className="text-center text-gray-500 mb-10" data-id="element-237">
            Acompañantes, cuidadores y profesionales de la salud
          </p>

          <div className="space-y-5" data-id="element-238">
            {/* Testimonial 1 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-239">
              <StarRating data-id="element-240" />
              <p className="text-gray-700 mt-3 leading-relaxed italic" data-id="element-241">
                "Trabajo con adultos mayores y este kit me re sirvió. Les gustan
                las actividades porque no son difíciles ni para chicos. Los veo
                más animados cuando hacemos los ejercicios."
              </p>
              <div className="mt-4 flex items-center gap-3" data-id="element-242">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{
                backgroundColor: 'var(--color-green-dark)',
                fontFamily: 'var(--font-heading)'
              }} data-id="element-243">
                  PM
                </div>
                <div data-id="element-244">
                  <p className="font-bold text-gray-900 text-sm" data-id="element-245">
                    Paula Medina
                  </p>
                  <p className="text-gray-500 text-xs" data-id="element-246">
                    Acompañante · Buenos Aires
                  </p>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-247">
              <StarRating data-id="element-248" />
              <p className="text-gray-700 mt-3 leading-relaxed italic" data-id="element-249">
                "Lo que más valoro es llegar al encuentro con todo preparado.
                Antes perdía tiempo buscando qué hacer. Ahora abro el kit y
                listo. Me cambió el día a día de trabajo."
              </p>
              <div className="mt-4 flex items-center gap-3" data-id="element-250">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{
                backgroundColor: 'var(--color-green-dark)',
                fontFamily: 'var(--font-heading)'
              }} data-id="element-251">
                  LR
                </div>
                <div data-id="element-252">
                  <p className="font-bold text-gray-900 text-sm" data-id="element-253">Luciana R.</p>
                  <p className="text-gray-500 text-xs" data-id="element-254">
                    Acompañante Terapéutica · Córdoba
                  </p>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-255">
              <StarRating data-id="element-256" />
              <p className="text-gray-700 mt-3 leading-relaxed italic" data-id="element-257">
                "Tengo pacientes con distintos niveles y el kit me permite
                adaptar fácil. Las instrucciones son claras y los adultos las
                entienden solos. Muy buen material."
              </p>
              <div className="mt-4 flex items-center gap-3" data-id="element-258">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{
                backgroundColor: 'var(--color-green-dark)',
                fontFamily: 'var(--font-heading)'
              }} data-id="element-259">
                  MG
                </div>
                <div data-id="element-260">
                  <p className="font-bold text-gray-900 text-sm" data-id="element-261">Marcela G.</p>
                  <p className="text-gray-500 text-xs" data-id="element-262">
                    Cuidadora domiciliaria · Rosario
                  </p>
                </div>
              </div>
            </div>

            {/* Testimonial 4 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-263">
              <StarRating data-id="element-264" />
              <p className="text-gray-700 mt-3 leading-relaxed italic" data-id="element-265">
                "Trabajo en un geriátrico y lo uso en grupos pequeños. Las
                actividades son perfectas para dinamizar los encuentros. Los
                abuelos se ríen, compiten y se nota el cambio en su estado de
                ánimo."
              </p>
              <div className="mt-4 flex items-center gap-3" data-id="element-266">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{
                backgroundColor: 'var(--color-green-dark)',
                fontFamily: 'var(--font-heading)'
              }} data-id="element-267">
                  VT
                </div>
                <div data-id="element-268">
                  <p className="font-bold text-gray-900 text-sm" data-id="element-269">Valeria T.</p>
                  <p className="text-gray-500 text-xs" data-id="element-270">
                    Trabajadora de geriátrico · Mendoza
                  </p>
                </div>
              </div>
            </div>

            {/* Testimonial 5 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-271">
              <StarRating data-id="element-272" />
              <p className="text-gray-700 mt-3 leading-relaxed italic" data-id="element-273">
                "Soy AT hace 6 años y nunca había encontrado un material tan
                completo y tan fácil de usar. El respaldo científico me permite
                incluirlo en mis informes sin problema. Lo recomiendo a todas
                mis colegas."
              </p>
              <div className="mt-4 flex items-center gap-3" data-id="element-274">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{
                backgroundColor: 'var(--color-green-dark)',
                fontFamily: 'var(--font-heading)'
              }} data-id="element-275">
                  NF
                </div>
                <div data-id="element-276">
                  <p className="font-bold text-gray-900 text-sm" data-id="element-277">Natalia F.</p>
                  <p className="text-gray-500 text-xs" data-id="element-278">
                    Acompañante Terapéutica · Tucumán
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── GUARANTEE ── */}
      <section className="w-full bg-white py-16 px-4" data-id="element-279">
        <div className="max-w-2xl mx-auto" data-id="element-280">
          <div className="border-2 border-green-200 rounded-2xl p-8 text-center bg-green-50" data-id="element-281">
            <div className="text-6xl mb-4" data-id="element-282">🛡️</div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 uppercase" style={{
            fontFamily: 'var(--font-heading)'
          }} data-id="element-283">
              Garantía de satisfacción — 7 días
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg" data-id="element-284">
              Si en los próximos 7 días sentís que el kit no te sirve en tu
              trabajo, nos escribís y te devolvemos el{' '}
              <strong data-id="element-285">100% de tu dinero</strong>. Sin preguntas, sin vueltas.
              Porque sabemos que cuando lo uses, va a quedar en tu caja de
              herramientas para siempre.
            </p>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="w-full bg-gray-50 py-16 px-4" data-id="element-286">
        <div className="max-w-2xl mx-auto" data-id="element-287">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4 uppercase" style={{
          fontFamily: 'var(--font-heading)'
        }} data-id="element-288">
            Preguntas frecuentes
          </h2>
          <p className="text-center text-gray-500 mb-10" data-id="element-289">
            Todo lo que necesitás saber antes de comprar
          </p>
          <FAQAccordion data-id="element-290" />
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="w-full py-16 px-4 text-white" style={{
      backgroundColor: 'var(--color-green-dark)'
    }} data-id="element-291">
        <div className="max-w-2xl mx-auto text-center" data-id="element-292">
          <h2 className="text-3xl md:text-4xl font-bold uppercase mb-4" style={{
          fontFamily: 'var(--font-heading)'
        }} data-id="element-293">
            Llegá a tu próximo encuentro con todo listo.
          </h2>
          <p className="text-green-200 text-lg mb-8 leading-relaxed" data-id="element-294">
            Sin improvisar. Sin buscar. Sin adaptaciones de último momento. El
            Kit Mente Clara & Activa es la herramienta que te acompaña en cada
            sesión.
          </p>

          <CTAButton className="w-full max-w-md mx-auto block text-xl py-6" data-id="element-295">
            ✅ ACCEDER AL KIT AHORA
          </CTAButton>

          <div className="flex items-center justify-center gap-6 mt-6 text-green-200 text-sm flex-wrap" data-id="element-296">
            <span className="flex items-center gap-1.5" data-id="element-297">
              <ShieldCheckIcon className="w-4 h-4" data-id="element-298" />
              Compra segura
            </span>
            <span className="flex items-center gap-1.5" data-id="element-299">
              <MailIcon className="w-4 h-4" data-id="element-300" />
              Acceso inmediato
            </span>
            <span className="flex items-center gap-1.5" data-id="element-301">
              <span data-id="element-302">🛡️</span>
              Garantía 7 días
            </span>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="w-full bg-gray-900 py-6 px-4 text-center text-gray-500 text-sm" data-id="element-303">
        <p data-id="element-304">© 2025 Kit Mente Clara & Activa. Todos los derechos reservados.</p>
        <p className="mt-1" data-id="element-305">
          Producto digital — Acceso inmediato vía correo electrónico
        </p>
      </footer>
    </div>;
}